import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { ageLabel } from "@/lib/age";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import FamilyFormFields from "@/components/FamilyFormFields";
import { toast } from "sonner";
import {
  Leaf,
  LogOut,
  Home,
  Plus,
  Users,
  Wallet,
  Baby,
  Search,
  MoreHorizontal,
  Link as LinkIcon,
  Trash2,
  Pencil,
  DollarSign,
  FileDown,
  Copy,
  RefreshCw,
  Mail,
  Send,
  Upload,
  Calendar as CalIcon,
  TreePine,
  Menu,
  ImageIcon,
  MessageCircle,
  MessageSquare,
  Newspaper,
  AlertTriangle,
} from "lucide-react";
import { familyEmailIssues, looksBroken } from "@/lib/emailCheck";

const EMPTY = {
  first_name: "",
  last_name: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  zip_code: "",
  phone: "",
  email: "",
  birth_date: "",
  father_name: "",
  mother_name: "",
  father_family_id: null,
  mother_family_id: null,
  children: [],
  notes: "",
  reunion_fee: 50,
};

const REUNION_TARGET_MS = new Date("2028-07-21T00:00:00").getTime();

function ReadinessPanel({ stats }) {
  const total = stats.families || 0;
  const paid = stats.paid || 0;
  const pctFamilies = total > 0 ? Math.round((paid / total) * 100) : 0;
  const collected = Number(stats.collected || 0);
  const daysLeft = Math.max(
    0,
    Math.ceil((REUNION_TARGET_MS - Date.now()) / (1000 * 60 * 60 * 24))
  );
  const bars = [
    {
      label: "Families paid",
      value: `${paid} / ${total}`,
      pct: pctFamilies,
      testid: "readiness-families",
    },
    {
      label: "Photos on memorials",
      value: `${stats.memorials_with_photo || 0} / ${stats.memorials || 0}`,
      pct: stats.memorials
        ? Math.round(((stats.memorials_with_photo || 0) / stats.memorials) * 100)
        : 0,
      testid: "readiness-photos",
    },
    {
      label: "Events scheduled",
      value: `${stats.events || 0}`,
      pct: Math.min(100, (stats.events || 0) * 25),
      testid: "readiness-events",
    },
  ];
  return (
    <div
      className="bg-white border border-[#e6e2d6] rounded-2xl p-6 mb-8 grid gap-6 md:grid-cols-[auto_1fr] items-center"
      data-testid="readiness-panel"
    >
      <div className="flex items-center gap-4 pr-6 md:border-r border-[#eae5d9]">
        <div className="text-center">
          <div className="font-serif text-4xl md:text-5xl text-[#1a2947] tabular-nums leading-none">
            {daysLeft}
          </div>
          <div className="text-[10px] tracking-[0.16em] uppercase text-stone-500 mt-2">
            days to Denver
          </div>
        </div>
        <div className="text-left">
          <div className="text-[10px] tracking-[0.16em] uppercase text-[#8a7a3a] font-semibold">
            Reunion readiness
          </div>
          <div className="font-serif text-2xl text-stone-900 mt-1">
            ${collected.toFixed(2)}
          </div>
          <div className="text-xs text-stone-500">collected · {stats.payments_logged || 0} ledger entries</div>
        </div>
      </div>
      <div className="space-y-3">
        {bars.map((b) => (
          <div key={b.label} data-testid={b.testid}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-stone-600">{b.label}</span>
              <span className="tabular-nums text-stone-800 font-medium">{b.value}</span>
            </div>
            <div className="h-2 rounded-full bg-[#f0ece0] overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#1a2947] to-[#7CB2E8] transition-[width] duration-500"
                style={{ width: `${b.pct}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [families, setFamilies] = useState([]);
  const [stats, setStats] = useState({ families: 0, paid: 0, unpaid: 0, collected: 0, children: 0 });
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null); // family object or null
  const [creating, setCreating] = useState(false);
  const [paying, setPaying] = useState(null); // family for payment modal
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [importing, setImporting] = useState(false);
  const [sendingBulk, setSendingBulk] = useState(false);
  const [smsPreview, setSmsPreview] = useState(null); // {rows, blob, total} or null
  const [showOnlyBrokenEmails, setShowOnlyBrokenEmails] = useState(false);
  const fileInputRef = React.useRef(null);

  const load = useCallback(async () => {
    try {
      const [f, s] = await Promise.all([api.get("/families"), api.get("/stats")]);
      setFamilies(f.data);
      setStats(s.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = families;
    if (showOnlyBrokenEmails) {
      list = list.filter((f) => familyEmailIssues(f).length > 0);
    }
    if (!q) return list;
    return list.filter((f) =>
      [f.first_name, f.last_name, f.email, f.phone, f.city, f.state]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [families, query, showOnlyBrokenEmails]);

  const brokenEmailCount = useMemo(
    () => families.filter((f) => familyEmailIssues(f).length > 0).length,
    [families]
  );

  const autoFixableCount = useMemo(() => {
    let n = 0;
    for (const f of families) {
      const p = looksBroken(f.email);
      const s = looksBroken(f.partner_email);
      if (p.broken && p.suggestion) n += 1;
      if (s.broken && s.suggestion) n += 1;
    }
    return n;
  }, [families]);

  const buildMagicLink = (token) =>
    `${window.location.origin}/family/${token}`;

  const copyLink = async (token) => {
    const link = buildMagicLink(token);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(link);
        toast.success("Link copied to clipboard");
        return;
      }
      throw new Error("no-clipboard");
    } catch {
      // Fallback: hidden textarea + execCommand
      try {
        const ta = document.createElement("textarea");
        ta.value = link;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success("Link copied to clipboard");
      } catch {
        window.prompt("Copy this family link:", link);
      }
    }
  };

  const copyInviteText = async (family) => {
    const link = buildMagicLink(family.magic_token);
    const name = (family.first_name || "").trim() || "there";
    const text =
      `Hi ${name} — you're invited to our family reunion! ` +
      `Please open your private card to update your details and RSVP: ${link}`;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast.success("Invite text copied — paste into iMessage, WhatsApp, etc.");
        return;
      }
      throw new Error("no-clipboard");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success("Invite text copied");
      } catch {
        window.prompt("Copy this invite text:", text);
      }
    }
  };

  const smsInvite = (family) => {
    const link = buildMagicLink(family.magic_token);
    const name = (family.first_name || "").trim() || "there";
    const body = `Hi ${name} — you're invited to our family reunion! Please open your private card to update your details and RSVP: ${link}`;
    // The `sms:` URI with `?&body=` works on both iOS and Android
    const phone = (family.phone || "").replace(/[^\d+]/g, "");
    const url = `sms:${phone}?&body=${encodeURIComponent(body)}`;
    window.location.href = url;
  };

  const regenLink = async (fid) => {
    try {
      const r = await api.post(`/families/${fid}/regenerate-link`);
      toast.success("Fresh link generated");
      await load();
      // If a modal is open for this family, refresh it
      if (editing && editing._id === fid) {
        setEditing({ ...editing, magic_token: r.data.magic_token });
      }
    } catch (e) {
      toast.error("Failed");
    }
  };

  const remove = async (fid) => {
    try {
      await api.delete(`/families/${fid}`);
      toast.success("Family removed");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  // Apply a suggested email-typo fix to a family via the update endpoint
  // (works for the primary email or the spouse email column).
  const applyEmailFix = async (family, which, suggestion) => {
    try {
      const key = which === "spouse" ? "partner_email" : "email";
      await api.put(`/families/${family._id}`, { ...family, [key]: suggestion });
      toast.success(
        `Updated ${family.first_name || "family"} ${family.last_name || ""}: ${suggestion}`.trim()
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not apply fix");
    }
  };

  const openEmailFixToast = (family, which) => {
    const email = which === "spouse" ? family.partner_email : family.email;
    const info = looksBroken(email);
    if (!info.broken) return;
    if (info.suggestion) {
      toast(`${which === "spouse" ? "Spouse email" : "Email"}: ${info.reason}`, {
        description: `${email}  →  ${info.suggestion}`,
        duration: 12000,
        action: {
          label: "Apply fix",
          onClick: () => applyEmailFix(family, which, info.suggestion),
        },
        cancel: { label: "Ignore", onClick: () => {} },
      });
    } else {
      toast.warning(
        `${which === "spouse" ? "Spouse email" : "Email"} looks broken`,
        { description: info.reason }
      );
    }
  };

  // Loops through every family and applies the auto-suggested email fix
  // (primary + spouse). Skips broken emails that have no confident
  // suggestion. Returns a summary toast.
  const applyAllEmailFixes = async () => {
    const targets = [];
    for (const f of families) {
      const primary = looksBroken(f.email);
      if (primary.broken && primary.suggestion) {
        targets.push({ family: f, which: "primary", suggestion: primary.suggestion });
      }
      const spouse = looksBroken(f.partner_email);
      if (spouse.broken && spouse.suggestion) {
        targets.push({ family: f, which: "spouse", suggestion: spouse.suggestion });
      }
    }
    if (targets.length === 0) {
      toast.info("No auto-fixable emails found.");
      return;
    }
    // Group multiple fixes for the same family into a single PUT so we
    // don't overwrite one field with a stale copy of another.
    const byFamily = new Map();
    for (const t of targets) {
      const patch = byFamily.get(t.family._id) || { ...t.family };
      const key = t.which === "spouse" ? "partner_email" : "email";
      patch[key] = t.suggestion;
      const fixList = byFamily.get(t.family._id)?._fixCount || 0;
      patch._fixCount = fixList + 1;
      byFamily.set(t.family._id, patch);
    }
    let familiesOk = 0;
    let fixesOk = 0;
    let failed = 0;
    for (const [fid, patch] of byFamily.entries()) {
      const fixCount = patch._fixCount || 1;
      // eslint-disable-next-line no-unused-vars
      const { _fixCount, ...cleanPatch } = patch;
      try {
        await api.put(`/families/${fid}`, cleanPatch);
        familiesOk += 1;
        fixesOk += fixCount;
      } catch {
        failed += 1;
      }
    }
    if (fixesOk > 0) {
      toast.success(
        `Applied ${fixesOk} fix${fixesOk === 1 ? "" : "es"} across ${familiesOk} famil${familiesOk === 1 ? "y" : "ies"}${failed ? ` · ${failed} failed` : ""}.`
      );
    } else {
      toast.error("Couldn't apply any fixes.");
    }
    load();
  };

  const downloadLabels = async () => {
    try {
      const t = localStorage.getItem("admin_token");
      const res = await fetch(`${API_BASE}/families/export/labels-pdf`, {
        credentials: "include",
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "reunion-labels-avery5160.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Labels PDF downloaded");
    } catch {
      toast.error("Failed to export labels");
    }
  };

  const downloadDirectory = async () => {
    try {
      const t = localStorage.getItem("admin_token");
      const res = await fetch(`${API_BASE}/families/export/directory-pdf`, {
        credentials: "include",
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "reunion-family-directory.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Family directory PDF downloaded");
    } catch {
      toast.error("Failed to export directory");
    }
  };

  const downloadPhoneTree = async () => {
    try {
      const t = localStorage.getItem("admin_token");
      const res = await fetch(`${API_BASE}/families/export/phone-tree-pdf`, {
        credentials: "include",
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "reunion-phone-tree.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Phone tree PDF downloaded");
    } catch {
      toast.error("Failed to export phone tree");
    }
  };

  const downloadTableTents = async () => {
    try {
      const t = localStorage.getItem("admin_token");
      const res = await fetch(`${API_BASE}/families/export/table-tents-pdf`, {
        credentials: "include",
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "reunion-table-tents.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Table tents PDF downloaded");
    } catch {
      toast.error("Failed to export table tents");
    }
  };

  const downloadSmsCsv = async () => {
    try {
      const t = localStorage.getItem("admin_token");
      const res = await fetch(`${API_BASE}/families/export/sms-csv`, {
        credentials: "include",
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!res.ok) throw new Error("Failed");
      const text = await res.text();
      const blob = new Blob([text], { type: "text/csv" });
      // Parse minimally — CSV has no embedded quotes in our writer output for these fields
      const lines = text.split(/\r?\n/).filter((l) => l.length > 0);
      const rows = lines.map((l) => l.split(","));
      const dataRows = rows.slice(1); // drop header
      setSmsPreview({
        rows: dataRows.slice(0, 3),
        total: dataRows.length,
        blob,
      });
    } catch {
      toast.error("Failed to export SMS list");
    }
  };

  const saveSmsBlob = () => {
    if (!smsPreview?.blob) return;
    const url = URL.createObjectURL(smsPreview.blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "reunion-sms-blast.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("SMS blast CSV downloaded");
    setSmsPreview(null);
  };


  const downloadCsvTemplate = async () => {
    try {
      const t = localStorage.getItem("admin_token");
      const res = await fetch(`${API_BASE}/families/export/csv-template`, {
        credentials: "include",
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "reunion-import-template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Couldn't download template");
    }
  };

  const importCsv = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const t = localStorage.getItem("admin_token");
      const res = await fetch(`${API_BASE}/families/import`, {
        method: "POST",
        credentials: "include",
        headers: t ? { Authorization: `Bearer ${t}` } : {},
        body: form,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Import failed");
      toast.success(
        `Imported ${data.created} famil${data.created === 1 ? "y" : "ies"}` +
          (data.errors?.length ? ` · ${data.errors.length} row error(s)` : "")
      );
      load();
    } catch (e) {
      toast.error(e.message || "Import failed");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const sendInvite = async (fid) => {
    try {
      await api.post(`/families/${fid}/send-invite`);
      toast.success("Invite sent");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
    }
  };

  const sendDigest = async () => {
    try {
      const r = await api.post("/digest/send-now");
      toast.success(`Digest emailed to ${r.data.to}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const sendAllInvites = async () => {
    setSendingBulk(true);
    try {
      const r = await api.post("/families/send-invites");
      const { sent, failed, errors } = r.data;
      if (sent === 0 && failed > 0 && errors?.length) {
        toast.error(`Couldn't send: ${errors[0].error}`);
      } else if (failed > 0) {
        toast.warning(
          `Sent ${sent} · ${failed} failed — ${errors?.[0]?.error || "see logs"}`
        );
      } else {
        toast.success(`Sent ${sent} invite${sent === 1 ? "" : "s"}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
    } finally {
      setSendingBulk(false);
    }
  };


  const nudgeCandidates = useMemo(() => {
    const cutoff = Date.now() - 14 * 24 * 60 * 60 * 1000;
    return families.filter((f) => {
      const invitedAt = f.last_invited_at ? new Date(f.last_invited_at).getTime() : 0;
      if (!invitedAt || invitedAt > cutoff) return false;
      const openedAt = f.last_opened_at ? new Date(f.last_opened_at).getTime() : 0;
      return !openedAt || openedAt < invitedAt;
    });
  }, [families]);

  const lastNudgedAt = useMemo(() => {
    let latest = 0;
    for (const f of families) {
      const t = f.last_nudged_at ? new Date(f.last_nudged_at).getTime() : 0;
      if (t > latest) latest = t;
    }
    return latest || null;
  }, [families]);

  const lastNudgedLabel = useMemo(() => {
    if (!lastNudgedAt) return "";
    const days = Math.floor((Date.now() - lastNudgedAt) / (24 * 60 * 60 * 1000));
    if (days === 0) return "Last nudged today";
    if (days === 1) return "Last nudged yesterday";
    if (days < 7) return `Last nudged ${days} days ago`;
    return `Last nudged ${new Date(lastNudgedAt).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    })}`;
  }, [lastNudgedAt]);

  const nudgeUnopened = async () => {
    if (nudgeCandidates.length === 0) {
      toast.info("No stale invites — every invited family has opened their card.");
      return;
    }
    setSendingBulk(true);
    try {
      const r = await api.post("/families/resend-unopened");
      const { nudged, sent, failed, errors } = r.data;
      if (sent === 0 && failed > 0 && errors?.length) {
        toast.error(`Couldn't nudge: ${errors[0].error}`);
      } else if (failed > 0) {
        toast.warning(
          `Nudged ${nudged} · sent ${sent} · ${failed} failed — ${errors?.[0]?.error || "see logs"}`
        );
      } else {
        toast.success(
          `Nudged ${nudged} famil${nudged === 1 ? "y" : "ies"} · ${sent} email${
            sent === 1 ? "" : "s"
          } sent`
        );
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to nudge");
    } finally {
      setSendingBulk(false);
    }
  };


  return (
    <div className="min-h-screen bg-[#fdfbf7]">
      {/* Top bar */}
      <header className="border-b border-[#e6e2d6] bg-white/70 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/bailey-logo.png" alt="Bailey Family Reunion" className="h-9 w-9 rounded-full object-cover ring-2 ring-[#1a2947]/10" />
            <span className="font-serif text-xl text-stone-900">Bailey Family Reunion</span>
            <span className="label-eyebrow text-stone-400 ml-2">Organizer</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 ml-8 mr-auto pl-8 border-l border-[#e6e2d6]">
            <Link
              to="/admin"
              className="text-sm text-stone-700 hover:text-[#1a2947] font-medium"
              data-testid="nav-families"
            >
              Families
            </Link>
            <Link
              to="/admin/events"
              className="text-sm text-stone-500 hover:text-[#1a2947]"
              data-testid="nav-events"
            >
              <CalIcon className="h-3 w-3 inline mr-1" /> Events
            </Link>
            <Link
              to="/admin/tree"
              className="text-sm text-stone-500 hover:text-[#1a2947]"
              data-testid="nav-tree"
            >
              <TreePine className="h-3 w-3 inline mr-1" /> Family Tree
            </Link>
            <Link
              to="/admin/gallery"
              className="text-sm text-stone-500 hover:text-[#1a2947]"
              data-testid="nav-gallery"
            >
              <ImageIcon className="h-3 w-3 inline mr-1" /> Gallery
            </Link>
          </nav>
          <div className="flex items-center gap-3">
            <span className="text-sm text-stone-500 hidden md:inline">
              {user?.email}
            </span>
            <Button
              variant="ghost"
              onClick={logout}
              className="text-stone-600 hover:text-[#1a2947]"
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-4 w-4 mr-1" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <div className="label-eyebrow mb-2 flex items-center gap-3">
              <span>Reunion 2026</span>
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 rounded-full border border-[#e6e2d6] bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#1a2947] hover:bg-[#1a2947] hover:text-white transition-colors"
                data-testid="admin-registry-home-btn"
              >
                <Home className="h-3 w-3" /> Home
              </Link>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl text-stone-900 tracking-tight">
              The Family Registry
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="file"
              accept=".csv"
              ref={fileInputRef}
              onChange={(e) => importCsv(e.target.files?.[0])}
              className="hidden"
              data-testid="csv-file-input"
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-full border-stone-300 btn-pill"
                  data-testid="import-menu-btn"
                >
                  <Upload className="h-4 w-4 mr-1" /> Import
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white">
                <DropdownMenuItem
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="import-csv-btn"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {importing ? "Importing..." : "Upload CSV"}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={downloadCsvTemplate} data-testid="download-template-btn">
                  <FileDown className="h-4 w-4 mr-2" /> Download CSV template
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              className="rounded-full border-stone-300 btn-pill"
              onClick={sendAllInvites}
              disabled={sendingBulk}
              data-testid="bulk-send-btn"
            >
              <Send className="h-4 w-4 mr-1" />
              {sendingBulk ? "Sending..." : "Email everyone"}
            </Button>
            {nudgeCandidates.length > 0 && (
              <div
                className="flex flex-col items-start"
                data-testid="nudge-cluster"
              >
                <Button
                  variant="outline"
                  className="rounded-full border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 btn-pill"
                  onClick={nudgeUnopened}
                  disabled={sendingBulk}
                  data-testid="nudge-unopened-btn"
                  title="Families invited 14+ days ago that never opened their card"
                >
                  <Send className="h-4 w-4 mr-1" />
                  Nudge {nudgeCandidates.length} unopened
                </Button>
                {lastNudgedLabel && (
                  <span
                    className="text-[11px] text-amber-800/80 mt-1 ml-3"
                    data-testid="nudge-last-history"
                  >
                    {lastNudgedLabel}
                  </span>
                )}
              </div>
            )}
            <Button
              variant="outline"
              className="rounded-full border-stone-300 btn-pill"
              onClick={sendDigest}
              data-testid="send-digest-btn"
            >
              <Newspaper className="h-4 w-4 mr-1" /> Email my digest
            </Button>
            <Button
              variant="outline"
              className="rounded-full border-stone-300 btn-pill"
              onClick={downloadLabels}
              data-testid="export-labels-btn"
            >
              <FileDown className="h-4 w-4 mr-1" /> Avery 5160 PDF
            </Button>
            <Button
              variant="outline"
              className="rounded-full border-stone-300 btn-pill"
              onClick={downloadDirectory}
              data-testid="export-directory-btn"
            >
              <FileDown className="h-4 w-4 mr-1" /> Directory PDF
            </Button>
            <Button
              variant="outline"
              className="rounded-full border-stone-300 btn-pill"
              onClick={downloadPhoneTree}
              data-testid="export-phone-tree-btn"
            >
              <FileDown className="h-4 w-4 mr-1" /> Phone tree PDF
            </Button>
            <Button
              variant="outline"
              className="rounded-full border-stone-300 btn-pill"
              onClick={downloadTableTents}
              data-testid="export-table-tents-btn"
            >
              <FileDown className="h-4 w-4 mr-1" /> Table tents PDF
            </Button>
            <Button
              variant="outline"
              className="rounded-full border-stone-300 btn-pill"
              onClick={async () => {
                try {
                  const t = localStorage.getItem("admin_token");
                  const res = await fetch(
                    `${API_BASE}/families/export/wallet-cards-bulk-pdf`,
                    { credentials: "include", headers: t ? { Authorization: `Bearer ${t}` } : {} }
                  );
                  if (!res.ok) throw new Error("Failed");
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "reunion-wallet-cards.pdf";
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success("Wallet cards PDF downloaded");
                } catch {
                  toast.error("Failed to export wallet cards");
                }
              }}
              data-testid="export-wallet-cards-btn"
            >
              <FileDown className="h-4 w-4 mr-1" /> Wallet cards (8-up)
            </Button>
            <Button
              variant="outline"
              className="rounded-full border-stone-300 btn-pill"
              onClick={downloadSmsCsv}
              data-testid="export-sms-csv-btn"
            >
              <FileDown className="h-4 w-4 mr-1" /> SMS blast CSV
            </Button>
            <Button
              className="rounded-full bg-[#1a2947] hover:bg-[#0f1a2e] btn-pill"
              onClick={() => setCreating(true)}
              data-testid="add-family-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> Add family
            </Button>
          </div>
        </div>

        <ReadinessPanel stats={stats} />

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
          <StatCard
            icon={<Users className="h-4 w-4" />}
            label="Families"
            value={stats.families}
            testid="stat-families"
          />
          <StatCard
            icon={<Baby className="h-4 w-4" />}
            label="Children"
            value={stats.children}
            testid="stat-children"
          />
          <StatCard
            icon={<Wallet className="h-4 w-4" />}
            label="Collected"
            value={`$${Number(stats.collected || 0).toFixed(2)}`}
            testid="stat-collected"
          />
          <StatCard
            icon={<DollarSign className="h-4 w-4" />}
            label="Families paid"
            value={`${stats.paid} / ${stats.unpaid + stats.paid}`}
            testid="stat-payments"
          />
          <StatCard
            icon={<Users className="h-4 w-4" />}
            label="Adults paid"
            value={stats.adults_paid ?? 0}
            testid="stat-adults-paid"
          />
          <StatCard
            icon={<Baby className="h-4 w-4" />}
            label="Kids paid"
            value={`${stats.kids_paid ?? 0} / ${stats.children ?? 0}`}
            testid="stat-kids-paid"
          />
        </div>

        {/* Search */}
        <div className="bg-white border border-[#e6e2d6] rounded-2xl p-3 mb-4 flex items-center gap-2">
          <Search className="h-4 w-4 text-stone-400 ml-2" />
          <Input
            placeholder="Search by name, city, phone or email..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="border-0 focus-visible:ring-0 bg-transparent"
            data-testid="admin-search-input"
          />
        </div>

        {/* Data cleanup alert — broken emails */}
        {brokenEmailCount > 0 && (
          <div
            data-testid="broken-emails-alert"
            className={`mb-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl border px-4 py-3 transition-colors ${
              showOnlyBrokenEmails
                ? "bg-amber-100 border-amber-300"
                : "bg-amber-50 border-amber-200"
            }`}
          >
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-amber-900">
                {brokenEmailCount} famil{brokenEmailCount === 1 ? "y" : "ies"} with
                broken email{brokenEmailCount === 1 ? "" : "s"}
              </div>
              <div className="text-xs text-amber-800/80">
                Common typos like <code className="font-mono">@gmailcom</code> or
                <code className="font-mono"> .con</code>
                {autoFixableCount > 0 ? (
                  <>
                    {" "}·{" "}
                    <span className="font-semibold">
                      {autoFixableCount} auto-fixable
                    </span>
                  </>
                ) : null}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {autoFixableCount > 0 && (
                <button
                  type="button"
                  onClick={applyAllEmailFixes}
                  data-testid="apply-all-email-fixes-btn"
                  className="rounded-full bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 py-1.5"
                >
                  Apply all fixes ({autoFixableCount})
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowOnlyBrokenEmails((v) => !v)}
                data-testid="toggle-broken-filter-btn"
                className="text-xs font-semibold text-amber-800 underline hover:text-amber-900"
              >
                {showOnlyBrokenEmails ? "Show all" : "Show only these"}
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-[#e6e2d6] rounded-2xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#faf7ef] hover:bg-[#faf7ef]">
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Children</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-stone-500">
                    No families yet. Click <b>Add family</b> to begin.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((f) => (
                <TableRow key={f._id} data-testid={`family-row-${f._id}`}>
                  <TableCell>
                    <div className="font-medium text-stone-900">
                      {f.first_name} {f.last_name}
                    </div>
                    {f.partner_first_name && (
                      <div className="text-xs text-stone-600 mt-0.5">
                        <span className="text-stone-400">Spouse:</span> {f.partner_first_name}{" "}
                        {f.partner_last_name || f.last_name}
                      </div>
                    )}
                    {f.other_adult_first_name && (
                      <div className="text-xs text-stone-600">
                        <span className="text-stone-400">Other:</span> {f.other_adult_first_name}{" "}
                        {f.other_adult_last_name || f.last_name}
                      </div>
                    )}
                    {f.birth_date && (
                      <div className="text-xs text-stone-500 mt-0.5">b. {f.birth_date}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-stone-600">
                    <div className="flex items-center gap-1.5">
                      <span>{f.email}</span>
                      {looksBroken(f.email).broken && (
                        <button
                          type="button"
                          onClick={() => openEmailFixToast(f, "primary")}
                          title={looksBroken(f.email).reason}
                          data-testid={`row-email-broken-${f._id}`}
                          className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 hover:bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 transition-colors"
                        >
                          <AlertTriangle className="h-3 w-3" />
                          {looksBroken(f.email).suggestion ? "Fix" : "Check"}
                        </button>
                      )}
                    </div>
                    <div className="text-stone-500">{f.phone}</div>
                    {(f.partner_email || f.partner_phone) && (
                      <div
                        className="text-xs text-stone-500 mt-1"
                        data-testid={`row-partner-contact-${f._id}`}
                      >
                        <span className="text-stone-400">Spouse:</span>{" "}
                        {[f.partner_email, f.partner_phone].filter(Boolean).join(" · ")}
                        {looksBroken(f.partner_email).broken && (
                          <button
                            type="button"
                            onClick={() => openEmailFixToast(f, "spouse")}
                            title={looksBroken(f.partner_email).reason}
                            data-testid={`row-partner-email-broken-${f._id}`}
                            className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 hover:bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 align-middle transition-colors"
                          >
                            <AlertTriangle className="h-3 w-3" />
                            {looksBroken(f.partner_email).suggestion ? "Fix" : "Check"}
                          </button>
                        )}
                      </div>
                    )}
                    {f.last_invited_at && (
                      <div
                        className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1"
                        data-testid={`row-last-invited-${f._id}`}
                        title={
                          f.partner_email && f.email
                            ? `Sent to ${f.email} and ${f.partner_email}`
                            : `Sent to ${f.email || f.partner_email}`
                        }
                      >
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        Invited{" "}
                        {new Date(f.last_invited_at).toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                        {f.email && f.partner_email && (
                          <span className="text-stone-400">· both adults</span>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-stone-600">
                    <div>{f.address_line1}</div>
                    <div className="text-stone-500">
                      {[f.city, f.state].filter(Boolean).join(", ")} {f.zip_code}
                    </div>
                  </TableCell>
                  <TableCell>
                    {(f.children || []).length === 0 ? (
                      <span className="text-xs text-stone-400">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1 max-w-[240px]">
                        {(f.children || []).map((c, i) => {
                          const age = ageLabel(c.birth_date);
                          return (
                            <span
                              key={`c-${f._id}-${i}`}
                              className="inline-flex items-center gap-1 text-[11px] bg-[#faf7ef] border border-[#e6e2d6] rounded-full px-2 py-0.5 text-stone-700"
                              title={c.birth_date ? `b. ${c.birth_date}` : ""}
                            >
                              <span>
                                {c.first_name}
                                {c.last_name && c.last_name !== f.last_name ? ` ${c.last_name}` : ""}
                              </span>
                              {age && (
                                <span
                                  className="rounded-full bg-[#eef2fb] border border-[#c7d3ea] px-1 py-0 text-[9px] font-semibold text-[#1a2947] tabular-nums"
                                  data-testid={`admin-child-age-${f._id}-${i}`}
                                >
                                  {age}
                                </span>
                              )}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const total =
                        (f.payment?.paid ? Number(f.payment.amount) || 0 : 0) +
                        (f.partner_paid ? Number(f.partner_paid_amount) || 0 : 0) +
                        (f.other_adult_paid ? Number(f.other_adult_paid_amount) || 0 : 0) +
                        (f.children || []).reduce(
                          (s, c) => s + (c.paid ? Number(c.paid_amount) || 0 : 0),
                          0
                        );
                      const anyPaid =
                        f.payment?.paid ||
                        f.partner_paid ||
                        f.other_adult_paid ||
                        (f.children || []).some((c) => c.paid);
                      if (!anyPaid) {
                        return (
                          <Badge variant="outline" className="border-[#1a2947] text-[#1a2947]">
                            Unpaid
                          </Badge>
                        );
                      }
                      const hasPartner = !!f.partner_first_name;
                      const hasOtherAdult = !!f.other_adult_first_name;
                      const kids = f.children || [];
                      const allPaid =
                        !!f.payment?.paid &&
                        (!hasPartner || f.partner_paid) &&
                        (!hasOtherAdult || f.other_adult_paid) &&
                        kids.every((c) => c.paid);
                      return (
                        <Badge
                          className={
                            allPaid
                              ? "bg-[#8a9a86] hover:bg-[#8a9a86] text-white"
                              : "bg-[#e5b143] hover:bg-[#e5b143] text-[#2c2a26]"
                          }
                        >
                          {allPaid ? "Paid" : "Partial"} ${total.toFixed(2)}
                        </Badge>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Copy family link"
                        onClick={() => copyLink(f.magic_token)}
                        data-testid={`row-copy-link-${f._id}`}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      {f.phone && (
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Text invite via SMS"
                          onClick={() => smsInvite(f)}
                          data-testid={`row-sms-${f._id}`}
                        >
                          <MessageSquare className="h-4 w-4" />
                        </Button>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`row-actions-${f._id}`}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-white">
                          <DropdownMenuItem onClick={() => setEditing(f)} data-testid={`action-edit-${f._id}`}>
                            <Pencil className="h-4 w-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setPaying(f)} data-testid={`action-payment-${f._id}`}>
                            <DollarSign className="h-4 w-4 mr-2" /> Mark payment
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => sendInvite(f._id)}
                            data-testid={`action-invite-${f._id}`}
                          >
                            <Mail className="h-4 w-4 mr-2" /> Email invite
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => copyLink(f.magic_token)} data-testid={`action-copy-${f._id}`}>
                            <Copy className="h-4 w-4 mr-2" /> Copy family link
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => copyInviteText(f)}
                            data-testid={`action-copy-invite-${f._id}`}
                          >
                            <MessageCircle className="h-4 w-4 mr-2" /> Copy invite text
                          </DropdownMenuItem>
                          {f.phone && (
                            <DropdownMenuItem
                              onClick={() => smsInvite(f)}
                              data-testid={`action-sms-${f._id}`}
                            >
                              <MessageSquare className="h-4 w-4 mr-2" /> Text invite via SMS
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => regenLink(f._id)} data-testid={`action-regen-${f._id}`}>
                            <RefreshCw className="h-4 w-4 mr-2" /> Regenerate link
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setConfirmDelete(f)}
                            className="text-red-600 focus:text-red-600"
                            data-testid={`action-delete-${f._id}`}
                          >
                            <Trash2 className="h-4 w-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </main>

      {/* Create modal */}
      <FamilyEditor
        open={creating}
        onOpenChange={(v) => setCreating(v)}
        title="Add a family"
        initial={EMPTY}
        onSave={async (payload) => {
          await api.post("/families", payload);
          toast.success("Family added");
          setCreating(false);
          load();
        }}
      />

      {/* Edit modal */}
      <FamilyEditor
        open={!!editing}
        onOpenChange={(v) => !v && setEditing(null)}
        title="Edit family"
        initial={editing || EMPTY}
        magicToken={editing?.magic_token}
        onCopyLink={() => editing && copyLink(editing.magic_token)}
        onRegenLink={() => editing && regenLink(editing._id)}
        onSave={async (payload) => {
          await api.put(`/families/${editing._id}`, payload);
          toast.success("Saved");
          setEditing(null);
          load();
        }}
      />

      {/* Payment modal */}
      <PaymentDialog
        family={paying}
        onClose={() => setPaying(null)}
        onSaved={() => {
          setPaying(null);
          load();
        }}
      />

      {/* Delete confirm */}
      <Dialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Remove this family?</DialogTitle>
          </DialogHeader>
          <p className="text-stone-600">
            This will permanently delete{" "}
            <b>{confirmDelete?.first_name} {confirmDelete?.last_name}</b>{" "}
            and their record. This can't be undone.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white rounded-full"
              onClick={() => remove(confirmDelete._id)}
              data-testid="confirm-delete-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!smsPreview} onOpenChange={(v) => !v && setSmsPreview(null)}>
        <DialogContent className="bg-white max-w-xl" data-testid="sms-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              SMS blast preview
            </DialogTitle>
          </DialogHeader>
          {smsPreview && smsPreview.total === 0 ? (
            <p className="text-stone-600" data-testid="sms-preview-empty">
              No phone numbers on file yet — add a phone to at least one family, then try again.
            </p>
          ) : smsPreview && (
            <>
              <p className="text-sm text-stone-600">
                {smsPreview.total} row{smsPreview.total === 1 ? "" : "s"} ready
                — showing the first {Math.min(3, smsPreview.total)} so you can
                sanity-check before opening the file.
              </p>
              <div
                className="mt-3 border border-[#e6e2d6] rounded-xl overflow-hidden text-sm"
                data-testid="sms-preview-table"
              >
                <div className="grid grid-cols-[1.2fr_1fr_1.5fr] bg-[#faf7ef] label-eyebrow px-3 py-2 border-b border-[#e6e2d6]">
                  <div>Name</div>
                  <div>Phone</div>
                  <div>Invite link</div>
                </div>
                {smsPreview.rows.map((r, i) => (
                  <div
                    key={`sms-row-${i}`}
                    className="grid grid-cols-[1.2fr_1fr_1.5fr] px-3 py-2 border-b border-[#eae5d9] last:border-0"
                    data-testid={`sms-preview-row-${i}`}
                  >
                    <div className="font-medium text-stone-800 truncate">
                      {r[0]}
                    </div>
                    <div className="text-stone-600 tabular-nums truncate">
                      {r[1]}
                    </div>
                    <div
                      className="text-stone-500 truncate"
                      title={r[2]}
                    >
                      {r[2]}
                    </div>
                  </div>
                ))}
              </div>
              {smsPreview.total > 3 && (
                <p className="text-xs text-stone-500 mt-2">
                  … and {smsPreview.total - 3} more row
                  {smsPreview.total - 3 === 1 ? "" : "s"} in the file.
                </p>
              )}
            </>
          )}
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setSmsPreview(null)}
              data-testid="sms-preview-cancel"
            >
              Cancel
            </Button>
            {smsPreview && smsPreview.total > 0 && (
              <Button
                className="rounded-full bg-[#1a2947] hover:bg-[#0f1a2e] btn-pill"
                onClick={saveSmsBlob}
                data-testid="sms-preview-download"
              >
                Download CSV
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon, label, value, testid }) {
  return (
    <div
      className="bg-white border border-[#e6e2d6] rounded-2xl p-5 card-lift"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-stone-500 mb-2">
        {icon}
        <span className="label-eyebrow">{label}</span>
      </div>
      <div className="font-serif text-3xl text-stone-900">{value}</div>
    </div>
  );
}

function FamilyEditor({ open, onOpenChange, title, initial, onSave, magicToken, onCopyLink, onRegenLink }) {
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue({ ...EMPTY, ...initial });
  }, [initial, open]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...value };
      delete payload._id;
      delete payload.magic_token;
      delete payload.payment;
      delete payload.created_at;
      delete payload.updated_at;
      await onSave(payload);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#fdfbf7] max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl">{title}</DialogTitle>
        </DialogHeader>

        {magicToken && (
          <div className="bg-white border border-[#e6e2d6] rounded-xl p-4 flex items-center justify-between gap-3 mb-2">
            <div className="min-w-0 flex-1">
              <div className="label-eyebrow mb-1">Private family link</div>
              <div className="text-sm text-stone-700 truncate">
                {window.location.origin}/family/{magicToken}
              </div>
              <div className="text-xs text-stone-500 mt-1">
                Scan the QR at the reunion to open this card on any phone.
              </div>
            </div>
            <img
              src={`${API_BASE}/families/token/${magicToken}/qr.png`}
              alt="QR code"
              className="h-24 w-24 rounded-lg border border-[#e6e2d6] bg-white shrink-0"
              data-testid="family-qr-preview"
            />
            <div className="flex flex-col gap-2 shrink-0">
              <Button variant="outline" onClick={onCopyLink} className="rounded-full" data-testid="editor-copy-link">
                <Copy className="h-4 w-4 mr-1" /> Copy
              </Button>
              <Button variant="ghost" onClick={onRegenLink} className="rounded-full">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        <FamilyFormFields value={value} onChange={setValue} />
        <DialogFooter className="pt-4 sticky bottom-0 -mx-6 px-6 pb-4 bg-[#fdfbf7]/95 backdrop-blur border-t border-[#e6e2d6] mt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="rounded-full bg-[#1a2947] hover:bg-[#0f1a2e] btn-pill"
            data-testid="editor-save-btn"
          >
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PaymentDialog({ family, onClose, onSaved }) {
  const [paid, setPaid] = useState(false);
  const [amount, setAmount] = useState(0);
  const [partnerPaid, setPartnerPaid] = useState(false);
  const [partnerAmount, setPartnerAmount] = useState(0);
  const [otherAdultPaid, setOtherAdultPaid] = useState(false);
  const [otherAdultAmount, setOtherAdultAmount] = useState(0);
  const [childRows, setChildRows] = useState([]);
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // Payment ledger (multiple payments with dates)
  const [ledger, setLedger] = useState([]);
  const [newEntry, setNewEntry] = useState({ amount: "", date: "", note: "" });
  const [ledgerBusy, setLedgerBusy] = useState(false);

  useEffect(() => {
    if (family) {
      setPaid(!!family.payment?.paid);
      setAmount(family.payment?.amount || 0);
      setPartnerPaid(!!family.partner_paid);
      setPartnerAmount(family.partner_paid_amount || 0);
      setOtherAdultPaid(!!family.other_adult_paid);
      setOtherAdultAmount(family.other_adult_paid_amount || 0);
      setChildRows(
        (family.children || []).map((c) => ({
          first_name: c.first_name || "",
          paid: !!c.paid,
          amount: c.paid_amount || 0,
        }))
      );
      setDate(family.payment?.date || new Date().toISOString().slice(0, 10));
      setNotes(family.payment?.notes || "");
      setLedger(family.payments || []);
      setNewEntry({ amount: "", date: new Date().toISOString().slice(0, 10), note: "" });
    }
  }, [family]);

  const setChildField = (i, k, v) => {
    const next = [...childRows];
    next[i] = { ...next[i], [k]: v };
    setChildRows(next);
  };

  const total =
    (paid ? Number(amount) || 0 : 0) +
    (partnerPaid ? Number(partnerAmount) || 0 : 0) +
    (otherAdultPaid ? Number(otherAdultAmount) || 0 : 0) +
    childRows.reduce((s, c) => s + (c.paid ? Number(c.amount) || 0 : 0), 0);

  const ledgerTotal = ledger.reduce((s, e) => s + (Number(e.amount) || 0), 0);

  const addLedgerEntry = async () => {
    const amt = Number(newEntry.amount);
    if (!amt || amt <= 0) return toast.error("Enter an amount greater than 0.");
    setLedgerBusy(true);
    try {
      const { data } = await api.post(`/families/${family._id}/payments`, {
        amount: amt,
        date: newEntry.date || new Date().toISOString().slice(0, 10),
        note: newEntry.note || "",
      });
      setLedger(data.payments || []);
      setNewEntry({ amount: "", date: new Date().toISOString().slice(0, 10), note: "" });
      toast.success("Payment logged");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to log payment");
    } finally {
      setLedgerBusy(false);
    }
  };

  const removeLedgerEntry = async (entryId) => {
    if (!window.confirm("Remove this payment entry?")) return;
    try {
      const { data } = await api.delete(`/families/${family._id}/payments/${entryId}`);
      setLedger(data.payments || []);
      toast.success("Removed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to remove");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/families/${family._id}/payment`, {
        paid,
        amount: Number(amount) || 0,
        date:
          paid || partnerPaid || otherAdultPaid || childRows.some((c) => c.paid)
            ? date
            : null,
        notes,
        partner_paid: partnerPaid,
        partner_paid_amount: Number(partnerAmount) || 0,
        other_adult_paid: otherAdultPaid,
        other_adult_paid_amount: Number(otherAdultAmount) || 0,
        children_paid: childRows.map((c, i) => ({
          index: i,
          paid: !!c.paid,
          amount: Number(c.amount) || 0,
        })),
      });
      toast.success("Payment updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const partnerLabel = family?.partner_first_name
    ? `${family.partner_first_name} ${family.partner_last_name || family.last_name || ""}`.trim()
    : null;
  const otherAdultLabel = family?.other_adult_first_name
    ? `${family.other_adult_first_name} ${family.other_adult_last_name || family.last_name || ""}`.trim()
    : null;
  const primaryLabel = `${family?.first_name || ""} ${family?.last_name || ""}`.trim() || "Primary adult";

  const PersonRow = ({ label, kind, checked, onCheck, amt, onAmt, testid }) => (
    <div
      className="flex items-center gap-3 py-2 border-b border-[#eae5d9] last:border-0"
      data-testid={`payment-row-${testid}`}
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-stone-800 truncate">{label}</div>
        <div className="text-[11px] uppercase tracking-wider text-stone-500">{kind}</div>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onCheck}
        data-testid={`payment-paid-switch-${testid}`}
      />
      <div className="w-28">
        <Input
          type="number"
          step="0.01"
          min="0"
          value={amt}
          onChange={(e) => onAmt(e.target.value)}
          disabled={!checked}
          placeholder="0.00"
          className="bg-stone-50 border-stone-200 h-9 text-sm"
          data-testid={`payment-amount-input-${testid}`}
        />
      </div>
    </div>
  );

  return (
    <Dialog open={!!family} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            Payments · The {family?.last_name} Family
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div
            className="bg-white border border-[#e6e2d6] rounded-xl p-4"
            data-testid="payment-ledger"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="label-eyebrow">Payment ledger</div>
              <div className="text-xs text-stone-500">
                Ledger total:{" "}
                <span
                  className="font-serif text-base text-[#1a2947]"
                  data-testid="payment-ledger-total"
                >
                  ${ledgerTotal.toFixed(2)}
                </span>
              </div>
            </div>

            {ledger.length === 0 ? (
              <div className="text-xs text-stone-400 py-2">
                No payments logged yet.
              </div>
            ) : (
              <div className="space-y-1 mb-3">
                {ledger.map((e) => (
                  <div
                    key={e.id}
                    className="flex items-center gap-2 text-sm py-1.5 border-b border-[#eae5d9] last:border-0"
                    data-testid={`payment-ledger-row-${e.id}`}
                  >
                    <div className="w-24 text-stone-500 tabular-nums">{e.date}</div>
                    <div className="w-20 font-medium text-stone-800 tabular-nums">
                      ${Number(e.amount).toFixed(2)}
                    </div>
                    <div className="flex-1 min-w-0 text-stone-500 truncate" title={e.note}>
                      {e.note || "—"}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeLedgerEntry(e.id)}
                      className="text-stone-400 hover:text-red-600 p-1"
                      title="Remove"
                      data-testid={`payment-ledger-remove-${e.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-4">
                <Label className="label-eyebrow text-[10px]">Date</Label>
                <Input
                  type="date"
                  value={newEntry.date}
                  onChange={(e) => setNewEntry({ ...newEntry, date: e.target.value })}
                  className="mt-1 h-9 text-sm bg-stone-50 border-stone-200"
                  data-testid="payment-ledger-new-date"
                />
              </div>
              <div className="col-span-3">
                <Label className="label-eyebrow text-[10px]">Amount</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={newEntry.amount}
                  onChange={(e) => setNewEntry({ ...newEntry, amount: e.target.value })}
                  className="mt-1 h-9 text-sm bg-stone-50 border-stone-200"
                  data-testid="payment-ledger-new-amount"
                />
              </div>
              <div className="col-span-3">
                <Label className="label-eyebrow text-[10px]">Note</Label>
                <Input
                  value={newEntry.note}
                  onChange={(e) => setNewEntry({ ...newEntry, note: e.target.value })}
                  placeholder="Venmo, check…"
                  className="mt-1 h-9 text-sm bg-stone-50 border-stone-200"
                  data-testid="payment-ledger-new-note"
                />
              </div>
              <div className="col-span-2">
                <Button
                  type="button"
                  onClick={addLedgerEntry}
                  disabled={ledgerBusy}
                  className="w-full h-9 rounded-full bg-[#1a2947] hover:bg-[#0f1a2e] text-sm"
                  data-testid="payment-ledger-add-btn"
                >
                  {ledgerBusy ? "…" : "Add"}
                </Button>
              </div>
            </div>
          </div>

          <div className="bg-[#faf7ef] border border-[#e6e2d6] rounded-xl px-4 py-3">
            <div className="label-eyebrow mb-2">Per-person dues</div>
            <PersonRow
              label={primaryLabel}
              kind="Primary adult"
              checked={paid}
              onCheck={setPaid}
              amt={amount}
              onAmt={setAmount}
              testid="primary"
            />
            {partnerLabel && (
              <PersonRow
                label={partnerLabel}
                kind="Spouse"
                checked={partnerPaid}
                onCheck={setPartnerPaid}
                amt={partnerAmount}
                onAmt={setPartnerAmount}
                testid="partner"
              />
            )}
            {otherAdultLabel && (
              <PersonRow
                label={otherAdultLabel}
                kind="Other adult"
                checked={otherAdultPaid}
                onCheck={setOtherAdultPaid}
                amt={otherAdultAmount}
                onAmt={setOtherAdultAmount}
                testid="other-adult"
              />
            )}
            {childRows.map((c, i) => (
              <PersonRow
                key={`child-${i}`}
                label={c.first_name || `Child ${i + 1}`}
                kind="Child"
                checked={c.paid}
                onCheck={(v) => setChildField(i, "paid", v)}
                amt={c.amount}
                onAmt={(v) => setChildField(i, "amount", v)}
                testid={`child-${i}`}
              />
            ))}
          </div>

          <div className="flex items-center justify-between bg-[#1a2947]/[0.05] border border-[#1a2947]/20 rounded-xl px-4 py-3">
            <span className="label-eyebrow">Total collected</span>
            <span
              className="font-serif text-2xl text-[#1a2947]"
              data-testid="payment-total"
            >
              ${total.toFixed(2)}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="label-eyebrow">Date</Label>
              <Input
                type="date"
                value={date || ""}
                onChange={(e) => setDate(e.target.value)}
                className="mt-2 bg-stone-50 border-stone-200"
                data-testid="payment-date-input"
              />
            </div>
            <div>
              <Label className="label-eyebrow">Notes</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-2 bg-stone-50 border-stone-200"
                placeholder="Check #, Venmo id, etc."
                data-testid="payment-notes-input"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            onClick={save}
            disabled={saving}
            className="rounded-full bg-[#1a2947] hover:bg-[#0f1a2e] btn-pill"
            data-testid="payment-save-btn"
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
